import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import Iframe from 'react-iframe';
import Constants from '../../../assests/Constants/constants';

function DocumentsByTypeModal({
  open,
  onClose,
  selectedDocumentType,
  documentsByType,
  direction,
  formatFileSize,
  onRemoveDocument,
  onRemoveExistingDocument,
  onFileSelect,
  onDocumentClick,
}) {
  const { t } = useTranslation();
  const [modalPreviewIndex, setModalPreviewIndex] = useState(0);
  const [showModalPreview, setShowModalPreview] = useState(true);

  // Reset preview index when modal opens or documents change
  useEffect(() => {
    if (open && documentsByType.length > 0) {
      setModalPreviewIndex(0);
      setShowModalPreview(true);
    }
  }, [open, documentsByType.length]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      documentsByType.forEach(doc => {
        if (doc.url && doc.url.startsWith('blob:')) {
          URL.revokeObjectURL(doc.url);
        }
      });
    };
  }, [documentsByType]);

  const docTypeInfo = Constants.AccountCardDocumentTypes.find(dt => dt.enumValue === selectedDocumentType);
  const docTypeLabel = docTypeInfo?.label || 'מסמכים';
  const currentModalDoc = documentsByType[modalPreviewIndex];
  const hasModalDocs = documentsByType.length > 0;

  const handleClose = () => {
    setModalPreviewIndex(0);
    setShowModalPreview(true);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={hasModalDocs && showModalPreview ? "lg" : "md"}
      fullWidth
      sx={{
        zIndex: 10010,
        '& .MuiDialog-root': {
          zIndex: 10010,
        },
        '& .MuiBackdrop-root': {
          zIndex: 10010,
        },
        '& .MuiDialog-container': {
          zIndex: 10010,
        },
      }}
      BackdropProps={{
        style: {
          zIndex: 10010,
          position: 'fixed',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          zIndex: 10011,
          position: 'relative',
          ...(hasModalDocs && showModalPreview && { width: '90%' }),
        },
      }}
    >
      <DialogTitle
        dir={direction}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#304FFF',
          color: 'white',
          fontWeight: 600,
          padding: '12px 16px',
        }}
      >
        <span>{docTypeLabel}</span>
        <IconButton 
          onClick={handleClose}
          size="small"
        >
          <CloseIcon sx={{ color: 'white' }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ padding: '16px' }}>
        <Box dir={direction} sx={{
          dir: direction,
          display: 'flex',
          flexDirection: hasModalDocs && showModalPreview ? 'row' : 'column',
          gap: 2,
          width: '100%',
        }}>
          {/* Documents List */}
          <Box sx={{ flex: 1, minWidth: hasModalDocs && showModalPreview ? '400px' : 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#304FFF' }}>
                {t("Documents") || "מסמכים"} ({documentsByType.length})
              </Typography>
              {hasModalDocs && (
                <IconButton onClick={() => setShowModalPreview(!showModalPreview)} size="small">
                  {showModalPreview ? <VisibilityOffIcon sx={{ color: "#304FFF" }} /> : <VisibilityIcon sx={{ color: "#304FFF" }} />}
                </IconButton>
              )}
            </Box>

            {/* Documents List - Table Format */}
            {documentsByType.length > 0 ? (
              <TableContainer 
                component={Paper} 
                sx={{ 
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid #e0e0e0',
                  maxHeight: '400px',
                  overflow: 'auto',
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#304FFF' }}>
                      <TableCell
                        sx={{
                          backgroundColor: '#304FFF',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '12px',
                          padding: '8px 12px',
                          borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        {t("DocumentType") || "סוג מסמך"}
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: '#304FFF',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '12px',
                          padding: '8px 12px',
                          borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        {t("Title") || "כותרת"}
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: '#304FFF',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '12px',
                          padding: '8px 12px',
                          borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        {t("Year") || "שנה"}
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: '#304FFF',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '12px',
                          padding: '8px 12px',
                          width: '80px',
                          textAlign: 'center',
                        }}
                      >
                        {t("Actions") || "פעולות"}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documentsByType.map((doc, index) => {
                      const docTypeName = Constants.AccountCardDocumentTypes.find(
                        dt => dt.enumValue === (doc.documentType !== undefined ? doc.documentType : doc.fullDoc?.DocumentType)
                      )?.label || 'מסמך';
                      
                      return (
                        <TableRow
                          key={doc.id}
                          sx={{
                            '&:hover': {
                              backgroundColor: doc.isExisting ? 'rgba(232, 245, 233, 0.5)' : 'rgba(227, 242, 253, 0.5)',
                            },
                            cursor: 'pointer',
                            borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                          }}
                          onClick={() => {
                            if (onDocumentClick) {
                              onDocumentClick(doc, index);
                            }
                            setModalPreviewIndex(index);
                            setShowModalPreview(true);
                          }}
                        >
                          <TableCell
                            sx={{
                              padding: '8px 10px',
                              borderRight: '1px solid rgba(224, 224, 224, 0.5)',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <InsertDriveFileIcon sx={{ 
                                color: doc.isExisting ? "#2E7D32" : "#1976D2", 
                                fontSize: 18 
                              }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: '12px',
                                  color: doc.isExisting ? "#1B5E20" : "#1565C0",
                                }}
                              >
                                {docTypeName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: '8px 10px',
                              borderRight: '1px solid rgba(224, 224, 224, 0.5)',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                fontSize: '12px',
                                color: doc.isExisting ? "#1B5E20" : "#1565C0",
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '200px',
                              }}
                              title={doc.title || doc.name}
                            >
                              {doc.title || doc.name}
                            </Typography>
                            {!doc.isExisting && doc.file && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#666",
                                  fontSize: "12px",
                                  display: 'block',
                                  mt: 0.5,
                                }}
                              >
                                {formatFileSize(doc.file.size)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: '8px 10px',
                              borderRight: '1px solid rgba(224, 224, 224, 0.5)',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                fontSize: '12px',
                                color: doc.isExisting ? "#1B5E20" : "#1565C0",
                              }}
                            >
                              {doc.year || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: '8px',
                              textAlign: 'center',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={async (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                
                                if (doc.isExisting) {
                                  // For existing documents, use the URL directly
                                  const urlToRemove = doc.fullDoc?.DocumentUrl || doc.fullDoc?.documentUrl || doc.url;
                                  if (urlToRemove && onRemoveExistingDocument) {
                                    await onRemoveExistingDocument(urlToRemove);
                                  }
                                } else if (!doc.isExisting) {
                                  if (onRemoveDocument) {
                                    onRemoveDocument(doc.id);
                                  }
                                }
                              }}
                              sx={{
                                color: "#d32f2f",
                                padding: "4px",
                                "&:hover": {
                                  backgroundColor: "rgba(211, 47, 47, 0.1)",
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3, color: '#9e9e9e' }}>
                <Typography variant="body2">
                  {t("NoDocuments") || "אין מסמכים"}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Document Preview */}
          {hasModalDocs && showModalPreview && currentModalDoc && (
            <Box
              dir={direction}
              sx={{
                flex: "0 0 50%",
                minHeight: "500px",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                overflow: "hidden",
                position: "relative",
              }}
            >
              {/* Navigation Arrows */}
              {modalPreviewIndex > 0 && (
                <IconButton
                  onClick={() => setModalPreviewIndex(prev => prev - 1)}
                  sx={{
                    position: "absolute",
                    left: direction === "rtl" ? "auto" : "10px",
                    right: direction === "rtl" ? "10px" : "auto",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    backgroundColor: "#304FFF",
                    color: "white",
                    "&:hover": { backgroundColor: "#1E40FF" },
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M14.9995 15.875L11.1195 11.995L14.9995 8.11498C15.3895 7.72498 15.3895 7.09498 14.9995 6.70498C14.6095 6.31498 13.9795 6.31498 13.5895 6.70498L8.99953 11.295C8.60953 11.685 8.60953 12.315 8.99953 12.705L13.5895 17.295C13.9795 17.685 14.6095 17.685 14.9995 17.295C15.3795 16.905 15.3895 16.265 14.9995 15.875Z" fill="white" />
                  </svg>
                </IconButton>
              )}

              {modalPreviewIndex < documentsByType.length - 1 && (
                <IconButton
                  onClick={() => setModalPreviewIndex(prev => prev + 1)}
                  sx={{
                    position: "absolute",
                    left: direction === "rtl" ? "10px" : "auto",
                    right: direction === "rtl" ? "auto" : "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    backgroundColor: "#304FFF",
                    color: "white",
                    "&:hover": { backgroundColor: "#1E40FF" },
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9.00047 15.875L12.8805 11.995L9.00047 8.11498C8.61047 7.72498 8.61047 7.09498 9.00047 6.70498C9.39047 6.31498 10.0205 6.31498 10.4105 6.70498L15.0005 11.295C15.3905 11.685 15.3905 12.315 15.0005 12.705L10.4105 17.295C10.0205 17.685 9.39047 17.685 9.00047 17.295C8.62047 16.905 8.61047 16.265 9.00047 15.875Z" fill="white" />
                  </svg>
                </IconButton>
              )}

              {/* Document Counter */}
              {documentsByType.length > 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "10px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 10,
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#304FFF",
                  }}
                >
                  {modalPreviewIndex + 1} / {documentsByType.length}
                </Box>
              )}

              {/* Iframe Preview */}
              <Iframe
                url={`${currentModalDoc.url}#page=1&view=FitH`}
                width="100%"
                height="100%"
                position="relative"
                title={currentModalDoc.name}
                allowFullScreen
              />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default DocumentsByTypeModal;

